import {DialogContent, Stack, Typography, useTheme} from '@mui/material';
import {BaseDialog, IconBadge} from '@resurs-internal/mui';
import {
  useCountry,
  useCustomer,
  useDepositAutogiro,
} from '@resurs-internal/rb-app-api';
import {useAlertDialog} from 'components';
import {useTranslate} from 'hooks';
import {useQueryClient} from '@tanstack/react-query';
import {useNavigate} from '@tanstack/react-router';
import {createBankIDUrl} from 'services/createBankIDUrl';
import {createRedirectPath} from 'services/uri/createRedirectPath';
import {ordinalDay} from '../utils/DepositeMonthlyDepositUtils';
import {MONTHLY_DEPOSIT_CALLBACK_PATH} from './DepositAccountMonthlyDepositCallbackPage';

type DepositAccountMonthlyDepositDialogCancelProps = {
  open: boolean;
  accountId: string | undefined;
  onClose: () => void;
  onDialogClose: () => void;
};

export const DepositAccountMonthlyDepositDialogCancel = ({
  open,
  accountId,
  onClose,
  onDialogClose,
}: DepositAccountMonthlyDepositDialogCancelProps) => {
  const theme = useTheme();
  const translate = useTranslate();
  const country = useCountry();
  const customer = useCustomer();
  const alert = useAlertDialog();
  const navigate = useNavigate();
  const client = useQueryClient();
  const autogiro = useDepositAutogiro(accountId);
  const autogiroData = autogiro.query.data;

  const closeAll = () => {
    onClose();
    onDialogClose();
  };

  const navigateToAccount = () => {
    if (!accountId) {
      closeAll();
      return;
    }

    closeAll();
    navigate({
      to: '/home/accounts/deposits/$accountId',
      params: {accountId},
    });
  };

  const showError = () => {
    alert.error({
      title: translate('savings.deposit.monthly.error.title'),
      subtitle: translate('common.error.message'),
    });
  };

  const handleConfirm = async () => {
    if (!accountId) return;

    const customerId = customer.info?.customerId;
    if (!customerId) {
      showError();
      return;
    }

    try {
      const isSEBankID = country.code === 'SE';
      const response = await autogiro.unRegister.mutateAsync({
        customerId: customerId.toString(),
        signingContext: {
          locale: country.locale,
          onSuccessUri: isSEBankID
            ? undefined
            : createRedirectPath(MONTHLY_DEPOSIT_CALLBACK_PATH, {
                accountId,
                status: 'SUCCESSFUL',
                action: 'cancel',
              }),
          onCancelledUri: isSEBankID
            ? undefined
            : createRedirectPath(MONTHLY_DEPOSIT_CALLBACK_PATH, {
                accountId,
                status: 'ERROR',
                action: 'cancel',
              }),
        },
      });

      if (isSEBankID) {
        closeAll();
        navigate({
          to: createBankIDUrl({
            token: response.signatureId,
            redirect: MONTHLY_DEPOSIT_CALLBACK_PATH,
            accountId,
            action: 'cancel',
          }),
        });
        return;
      }

      if (response.redirect) {
        window.location.href = response.redirect;
        return;
      }

      await Promise.all([
        autogiro.query.refetch(),
        client.invalidateQueries({queryKey: ['accounts', 'deposit']}),
        client.resetQueries({queryKey: ['account', 'deposit']}),
      ]);

      navigateToAccount();
      alert.success({
        title: translate('savings.deposit.monthly.cancel.success-title'),
      });
    } catch {
      showError();
    }
  };

  const formattedAmount = country.formatAmount(
    autogiroData?.withdrawalAmount ?? {
      amount: 0,
      currency: country.currency,
    },
  );
  const withdrawalDayOfMonth = autogiroData?.withdrawalDayOfMonth ?? 0;

  return (
    <BaseDialog
      open={open}
      onClose={() => {
        if (autogiro.unRegister.isPending) return;
        onClose();
      }}
      maxWidth={'sm'}
      header={translate('savings.deposit.monthly.cancel.title')}
      actions={[
        {
          title: translate('savings.deposit.monthly.cancel.cancel'),
          onClick: onClose,
          disabled: autogiro.unRegister.isPending,
        },
        {
          color: 'primary',
          title: translate('savings.deposit.monthly.cancel.confirm'),
          onClick: handleConfirm,
          disabled: autogiro.unRegister.isPending,
        },
      ]}>
      <DialogContent>
        <Stack spacing={3} alignItems={'center'}>
          <IconBadge
            type={'font'}
            icon={'triangle-exclamation'}
            color={theme.palette.warning.main}
            size={'xl'}
          />
          <Typography textAlign={'center'} variant={'h4'}>
            {translate('savings.deposit.monthly.cancel.title')}
          </Typography>
          <Typography
            textAlign={'center'}
            variant={'body1'}
            color={theme.palette.text.secondary}>
            {translate('savings.deposit.monthly.cancel.body', [
              formattedAmount,
              ordinalDay(withdrawalDayOfMonth, country.code),
            ])}
          </Typography>
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
